/**
 * Part 0 — EnrichmentDataChip + EnrichmentContextStrip
 *
 * EnrichmentDataChip: a compact "✓ value · via SEC EDGAR" pill placed next to a
 * pre-filled input so users SEE that the field was auto-populated from the
 * organization profile (and where it came from).
 *
 * EnrichmentContextStrip: a one-line summary of the key facts we already know
 * (industry, HQ, size, frameworks) rendered as chips. Dropped into module
 * pages (and inside AISuggestionsBanner) so the org context is always visible.
 */
import { CheckCircle2, Sparkles } from "lucide-react";
import { SOURCE_LABELS } from "../utils/enrichmentContext";
import { AttributeInfoIcon } from "./enrichmentTooltips";

export function EnrichmentDataChip({ value, source, label, testId, className = "", attribute }) {
  if (value === null || value === undefined || value === "") return null;
  const srcLabel = source ? (SOURCE_LABELS[source] || source) : null;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-medium ${className}`}
      title={srcLabel ? `Auto-filled${label ? ` ${label}` : ""} via ${srcLabel}` : "Auto-filled from your organization profile"}
      data-testid={testId || "enrichment-data-chip"}
    >
      <CheckCircle2 className="w-3 h-3 shrink-0" />
      <span className="truncate max-w-[180px]">{String(value)}</span>
      {srcLabel && <span className="text-emerald-500/80">· {srcLabel}</span>}
      {attribute && <AttributeInfoIcon attribute={attribute} source={source} className="ml-0.5 text-emerald-500 hover:text-emerald-700" />}
    </span>
  );
}

/** Build the short list of headline facts from a context object. */
function headlineFacts(ctx) {
  if (!ctx) return [];
  const prov = ctx.provenance || {};
  const facts = [];
  const ind = ctx.industry || {};
  const size = ctx.size || {};
  const hq = ctx.headquarters || {};
  if (ind.sector || ind.naics_primary) {
    facts.push({
      key: "industry",
      attribute: "industry_sector",
      value: ind.sector || `NAICS ${ind.naics_primary}`,
      source: (prov.naics_primary || {}).source,
    });
  }
  if (size.employee_band) {
    facts.push({ key: "size", attribute: "employee_size", value: `${size.employee_band} employees`, source: (prov.employee_band || {}).source });
  }
  if (size.revenue_band) {
    facts.push({ key: "revenue", attribute: "revenue_band", value: `${size.revenue_band} revenue`, source: null });
  }
  if (hq.city) {
    facts.push({ key: "hq", attribute: "headquarters", value: [hq.city, hq.state, hq.country].filter(Boolean).join(", "), source: (prov.headquarters || {}).source });
  }
  const fw = ctx.regulatory_frameworks || [];
  if (fw.length) {
    facts.push({ key: "frameworks", attribute: "regulatory_frameworks", value: `${fw.length} framework${fw.length > 1 ? "s" : ""}: ${fw.slice(0, 3).join(", ")}${fw.length > 3 ? "…" : ""}`, source: "rules_engine" });
  }
  return facts;
}

export function EnrichmentContextStrip({ context, className = "", testId = "enrichment-context-strip" }) {
  const ctx = context;
  if (!ctx || !ctx.has_enrichment) return null;
  const facts = headlineFacts(ctx);
  if (facts.length === 0) return null;
  return (
    <div
      className={`flex flex-wrap items-center gap-2 rounded-lg border border-emerald-100 bg-emerald-50/50 px-3 py-2 ${className}`}
      data-testid={testId}
    >
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700">
        <Sparkles className="w-3.5 h-3.5" />
        Using your organization profile
      </span>
      {facts.map((f) => (
        <EnrichmentDataChip key={f.key} value={f.value} source={f.source} attribute={f.attribute} testId={`enrichment-fact-${f.key}`} />
      ))}
    </div>
  );
}

export default EnrichmentDataChip;
